<template>
  <a-drawer width="35%" :label-col="4" :wrapper-col="14" :visible="open" @close="onClose">
    <a-divider orientation="left">
      <b>{{ formTitle }}</b>
    </a-divider>
    <a-form-model ref="form" :model="form" :rules="rules">
      <a-form-model-item :label="$t('views.bizUser.createAccount.email')" prop="email" >
        <a-input v-model="form.email" :placeholder="$t('views.bizUser.createAccount.emailPlaceholder')" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.bizUser.createAccount.nickname')" prop="nickname" >
        <a-input v-model="form.nickname" :placeholder="$t('views.bizUser.createAccount.nicknamePlaceholder')" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.bizUser.createAccount.password')" prop="password" >
        <a-input-password v-model="form.password" :placeholder="$t('views.bizUser.createAccount.passwordPlaceholder')" :maxLength="20" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.bizUser.createAccount.confirmPassword')" prop="confirmPassword" >
        <a-input-password v-model="form.confirmPassword" :placeholder="$t('views.bizUser.createAccount.confirmPasswordPlaceholder')" :maxLength="20" />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.bizUser.vipPayment.isVip')" prop="isVip" >
        <a-select v-model="form.isVip" :placeholder="$t('views.bizUser.vipPayment.isVipPlaceholder')" style="width: 100%" allow-clear>
          <a-select-option v-for="opt in isVipOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('views.bizUser.vipPayment.paymentMethod')" prop="paymentMethod" >
        <a-select v-model="form.paymentMethod" :placeholder="$t('views.bizUser.vipPayment.paymentMethodPlaceholder')" style="width: 100%" allow-clear>
          <a-select-option v-for="opt in paymentMethodOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item v-if="form.paymentMethod === 1" :label="$t('views.bizUser.vipPayment.firstRate')" prop="firstModelRate" >
        <a-input-number
          v-model="form.firstModelRate"
          :min="0.01"
          :max="1"
          :precision="2"
          :step="0.01"
          style="width: 100%"
          :placeholder="$t('views.bizUser.vipPayment.firstRatePlaceholder')"
        />
      </a-form-model-item>
      <a-form-model-item :label="$t('views.bizUser.createAccount.status')" prop="status" >
        <a-select v-model="form.status" :placeholder="$t('views.bizUser.query.userStatusPlaceholder')" style="width: 100%">
          <a-select-option v-for="(d, index) in statusOptions" :key="index" :value="Number(d.value)">{{ d.label }}</a-select-option>
        </a-select>
      </a-form-model-item>
      <div class="bottom-control">
        <a-space>
          <a-button type="primary" :loading="submitLoading" @click="submitForm">
            {{ $t('views.bizUser.createAccount.save') }}
          </a-button>
          <a-button type="dashed" @click="cancel">
            {{ $t('views.bizUser.createAccount.cancel') }}
          </a-button>
        </a-space>
      </div>
    </a-form-model>
  </a-drawer>
</template>

<script>
import { addUser } from '@/api/biz/user'

export default {
  name: 'CreateAccountForm',
  props: {
    statusOptions: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      submitLoading: false,
      open: false,
      form: {
        email: null,
        nickname: null,
        mobile: null,
        password: null,
        confirmPassword: null,
        isVip: 0,
        paymentMethod: 0,
        firstModelRate: null,
        status: 1
      }
    }
  },
  computed: {
    formTitle () {
      return this.$t('views.bizUser.createAccount.title')
    },
    isVipOptions () {
      return [
        { value: 0, label: this.$t('views.bizUser.option.notPro') },
        { value: 1, label: this.$t('views.bizUser.option.pro') }
      ]
    },
    paymentMethodOptions () {
      return [
        { value: 0, label: this.$t('views.bizUser.option.paymentFull') },
        { value: 1, label: this.$t('views.bizUser.option.paymentFirst') },
        { value: 2, label: this.$t('views.bizUser.option.paymentMonthly') }
      ]
    },
    rules () {
      const vm = this
      const validatePassword = (rule, value, callback) => {
        if (!value) {
          callback(new Error(vm.$t('views.bizUser.resetPwd.err.newPwdRequired')))
        } else if (!/^(?![0-9]+$)(?![a-zA-Z]+$)[0-9a-zA-Z]{6,12}$/.test(value)) {
          callback(new Error(vm.$t('views.bizUser.resetPwd.err.newPwdPattern')))
        } else {
          if (vm.form.confirmPassword && vm.$refs.form) {
            vm.$refs.form.validateField('confirmPassword')
          }
          callback()
        }
      }
      const validateConfirmPassword = (rule, value, callback) => {
        if (!value) {
          callback(new Error(vm.$t('views.bizUser.resetPwd.err.confirmRequired')))
        } else if (value !== vm.form.password) {
          callback(new Error(vm.$t('views.bizUser.resetPwd.err.confirmMismatch')))
        } else {
          callback()
        }
      }
      const validateFirstModelRate = (rule, value, callback) => {
        if (vm.form.paymentMethod !== 1) return callback()
        if (value === undefined || value === null || value === '') {
          return callback(new Error(vm.$t('views.bizUser.vipPayment.err.firstRateRequired')))
        }
        const num = Number(value)
        if (Number.isNaN(num)) return callback(new Error(vm.$t('views.bizUser.vipPayment.err.firstRateNumber')))
        if (num <= 0) return callback(new Error(vm.$t('views.bizUser.vipPayment.err.firstRateGt0')))
        if (num > 1) return callback(new Error(vm.$t('views.bizUser.vipPayment.err.firstRateLte1')))
        if (!/^\d+(\.\d{1,2})?$/.test(String(value))) {
          return callback(new Error(vm.$t('views.bizUser.vipPayment.err.firstRateDecimal')))
        }
        return callback()
      }
      return {
        email: [
          { required: true, message: this.$t('views.bizUser.createAccount.rule.emailRequired'), trigger: 'blur' },
          { type: 'email', message: this.$t('views.bizUser.createAccount.rule.emailFormat'), trigger: 'blur' }
        ],
        nickname: [
          { required: true, message: this.$t('views.bizUser.createAccount.rule.nicknameRequired'), trigger: 'blur' }
        ],
        password: [{ required: true, validator: validatePassword, trigger: 'change' }],
        confirmPassword: [{ required: true, validator: validateConfirmPassword, trigger: 'change' }],
        isVip: [{ required: true, message: this.$t('views.bizUser.vipPayment.rule.selectIsVip'), trigger: 'change' }],
        paymentMethod: [{ required: true, message: this.$t('views.bizUser.vipPayment.rule.selectPayment'), trigger: 'change' }],
        firstModelRate: [{ validator: validateFirstModelRate, trigger: ['change', 'blur'] }],
        status: [{ required: true, message: this.$t('views.bizUser.createAccount.rule.statusRequired'), trigger: 'change' }]
      }
    }
  },
  methods: {
    onClose () {
      this.open = false
    },
    cancel () {
      this.open = false
      this.reset()
    },
    reset () {
      this.form = {
        email: null,
        nickname: null,
        password: null,
        confirmPassword: null,
        isVip: 0,
        paymentMethod: 0,
        firstModelRate: null,
        status: 1
      }
    },
    handleAdd () {
      this.reset()
      this.open = true
    },
    submitForm: function () {
      this.$refs.form.validate(valid => {
        if (valid) {
          this.submitLoading = true
          const payload = {
            email: this.form.email,
            nickname: this.form.nickname,
            password: this.form.password,
            isVip: this.form.isVip,
            paymentMethod: this.form.paymentMethod,
            status: this.form.status,
            isNotice: true
          }
          if (this.form.mobile) {
            payload.mobile = this.form.mobile
          }
          if (this.form.paymentMethod === 1) {
            payload.firstModelRate = this.form.firstModelRate
          }
          addUser(payload).then(response => {
            if (response.success) {
              this.$message.success(this.$t('views.bizUser.createAccount.msg.success'), 3)
              this.open = false
              this.$emit('ok')
            } else {
              const messageInfo = response.message || response.msg || this.$t('views.bizUser.createAccount.msg.fail')
              this.$message.error(messageInfo)
            }
          }).finally(() => {
            this.submitLoading = false
          })
        } else {
          return false
        }
      })
    }
  }
}
</script>
